/**
 * NESW Bundles V2 — behavior for the two PDP bundle surfaces.
 *
 *   snippets/nesw-bundle-tiers-v2.liquid    → [data-btier]  buy-more ladder
 *   snippets/nesw-bundle-pairing-v2.liquid  → [data-bpair]  pair offer card
 *
 * Both snippets ship their own <script src> tag, so this file can execute
 * twice on one page. Everything below is idempotent: the module guards on
 * window.__neswBundlesV2 and every root guards on its own dataset flag.
 *
 * DISPLAY ONLY. Pricing is decided by the Ignis bundle discount function at
 * checkout — every number here is a prediction of what that function will do,
 * computed with the published display math (contract v1, all cents integers):
 *
 *   tier   amount  = cents off EACH unit          percent = whole percent
 *   pair   amount  = cents off the PAIR TOTAL, split in proportion to unit
 *                    price, remainder to the second line so nothing is lost
 *   clamps  percent capped at 90 (negative → 0); discount never exceeds the
 *           unit price
 *
 * Cart protocol: a pair is added as ONE batched /cart/add.js call through
 * window.neswAddToCartItems, both lines carrying the same fresh `_bundle_id`
 * plus `_bundle_kind: 'pair'`. Tier lines carry no properties at all — any N
 * units of a product pool automatically.
 *
 * APPEARANCE. Colours, radii, layout and the button wording come from the
 * Ignis bundles appearance config and are applied by the snippets, in Liquid
 * and in CSS. Exactly one of them reaches this file: the pair CTA is rebuilt
 * here every time the totals move, so the wording has to be rebuilt with it,
 * and it arrives on the root as data-cta-label. Everything else is presented
 * without JS on purpose, so nothing about how a card LOOKS depends on this
 * script having run.
 */
(function () {
  if (window.__neswBundlesV2) return;
  window.__neswBundlesV2 = true;

  /* ---------------------------------------------------------------- utils */

  var AMT = '{' + '{amount}' + '}';

  /* Local money formatter (same token set as assets/money-format-handler.js).
     Deliberately NOT Shopify.formatMoney: that file is deferred later in the
     document than these widgets, so it is not guaranteed to exist at init. */
  function makeMoney(format) {
    var fmt = format || ('$' + AMT);
    function withDelims(cents, precision, thousands, decimal) {
      var n = (cents / 100).toFixed(precision);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    return function (cents) {
      cents = Math.round(cents || 0);
      var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      var value;
      switch (m && m[1]) {
        case 'amount_no_decimals': value = withDelims(cents, 0, ',', '.'); break;
        case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
        case 'amount': value = withDelims(cents, 2, ',', '.'); break;
        default: return '$' + (cents / 100).toFixed(2);
      }
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
    };
  }

  function json(el, attr, fallback) {
    try { return JSON.parse(el.getAttribute(attr)); } catch (e) { return fallback; }
  }
  function num(v) { var n = parseInt(v, 10); return isNaN(n) ? 0 : n; }
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function track(name, payload) {
    try { if (window.pxl && window.pxl.track) window.pxl.track(name, payload); } catch (e) {}
  }
  function newBundleId() {
    return 'ig-pair-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* Cents off ONE unit for a tier. `unit` is the price the shopper pays today
     (post store sale), so a percent tier lands on the same base the pricing
     function uses when sale stacking is on. */
  function tierOffPerUnit(unit, type, value) {
    var off;
    if (type === 'percent') {
      var pct = Math.max(0, Math.min(90, value));
      off = Math.round(unit * pct / 100);
    } else {
      off = value;
    }
    return Math.max(0, Math.min(off, unit));
  }

  /* Cents off each side of a pair. Returns [offA, offB]. */
  function pairOff(unitA, unitB, type, value) {
    var offA, offB;
    if (type === 'percent') {
      var pct = Math.max(0, Math.min(90, value));
      offA = Math.round(unitA * pct / 100);
      offB = Math.round(unitB * pct / 100);
    } else {
      var total = unitA + unitB;
      offA = total > 0 ? Math.round(value * unitA / total) : 0;
      offB = value - offA;
    }
    offA = Math.max(0, Math.min(offA, unitA));
    offB = Math.max(0, Math.min(offB, unitB));
    return [offA, offB];
  }

  /* The PDP's current variant id. Same hidden input the theme's own ATC price
     poller reads (snippets/product-variants-data.liquid). */
  function pdpVariantId() {
    var el = document.getElementById('ultimate-selected-variant');
    return el ? num(el.value) : 0;
  }

  /* ------------------------------------------------------- 1. tier ladder */

  function initTiers(root) {
    if (root.dataset.bxInit) return;
    root.dataset.bxInit = '1';

    var money = makeMoney(root.dataset.moneyFormat);
    var showPercent = root.dataset.display === 'percent';
    var saleCents = num(root.dataset.saleCents);
    var variants = json(root, 'data-variants', []) || [];
    var cards = root.querySelectorAll('[data-btier-card]');
    var note = root.querySelector('[data-btier-note]');
    var msg = root.querySelector('[data-bx-msg]');
    var lastId = null;

    function currentVariant() {
      var id = pdpVariantId();
      var found = null;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) { found = variants[i]; break; }
      }
      return found || variants[0] || null;
    }

    function render() {
      var v = currentVariant();
      var unitGross = v ? v.price : num(root.dataset.basePrice);
      var unitNow = Math.max(0, unitGross - saleCents);
      var buyable = !!(v && v.available);

      each(cards, function (card) {
        var qty = num(card.dataset.qty);
        var type = card.dataset.discountType || '';
        var value = num(card.dataset.discountValue);
        var off = qty > 1 && type ? tierOffPerUnit(unitNow, type, value) : 0;

        var wasTotal = unitGross * qty;
        var nowTotal = (unitNow - off) * qty;
        var save = wasTotal - nowTotal;

        var wasEl = card.querySelector('[data-btier-was]');
        var nowEl = card.querySelector('[data-btier-now]');
        var saveEl = card.querySelector('[data-btier-save]');
        var eachEl = card.querySelector('[data-btier-each]');

        if (wasEl) wasEl.textContent = save > 0 ? money(wasTotal) : '';
        if (nowEl) nowEl.textContent = money(nowTotal);
        if (eachEl) eachEl.textContent = qty > 1 ? money(nowTotal / qty) + ' each' : '';
        if (saveEl) {
          if (save <= 0) {
            saveEl.textContent = '';
          } else if (showPercent) {
            saveEl.textContent = 'Save ' + Math.round(save / wasTotal * 100) + '%';
          } else {
            saveEl.textContent = 'Save ' + money(save);
          }
        }
        card.disabled = !buyable;
      });

      if (note) {
        note.textContent = buyable
          ? 'Savings apply automatically in your cart.'
          : 'This size is sold out. Pick another to bundle.';
      }
      if (msg) msg.textContent = '';
    }

    each(cards, function (card) {
      card.addEventListener('click', function () {
        var v = currentVariant();
        if (!v || !v.available) return;
        var qty = num(card.dataset.qty) || 1;
        var label = card.querySelector('[data-btier-now]');
        var before = label ? label.textContent : '';
        card.disabled = true;
        if (label) label.textContent = 'Adding';
        window.neswAddToCartItems([{ id: v.id, quantity: qty }], { openDrawer: true })
          .then(function () {
            track('bundle_add_to_cart', { bundle_kind: 'tier', units: qty, lines: 1 });
            if (label) label.textContent = before;
            card.disabled = false;
          })
          .catch(function () {
            if (label) label.textContent = before;
            card.disabled = false;
            if (msg) msg.textContent = 'Could not add that quantity. Some units may be out of stock.';
          });
      });
    });

    render();
    /* Follow the PDP's variant the same way the theme's own price poller does
       (350ms there; 400ms here is plenty for a display-only ladder). */
    setInterval(function () {
      var id = pdpVariantId();
      if (id !== lastId) { lastId = id; render(); }
    }, 400);
  }

  /* --------------------------------------------------------- 2. pair card */

  function initPair(root) {
    if (root.dataset.bxInit) return;
    root.dataset.bxInit = '1';

    var money = makeMoney(root.dataset.moneyFormat);
    var dType = root.dataset.discountType || 'amount';
    var dValue = num(root.dataset.discountValue);
    var cta = root.querySelector('[data-bpair-cta]');
    var msg = root.querySelector('[data-bx-msg]');
    /* The merchant's own button wording, from the Ignis appearance config.
       The snippet already rendered it server-side; this is the same string,
       for the rebuild below. 🔴 It is merchant free text, so it is only ever
       written as a TEXT NODE, never through innerHTML. */
    var ctaLabel = root.getAttribute('data-cta-label') || 'Add bundle to cart';

    var rows = Array.prototype.map.call(root.querySelectorAll('[data-bpair-row]'), function (el) {
      return {
        el: el,
        role: el.dataset.role,
        saleCents: num(el.dataset.saleCents),
        basePrice: num(el.dataset.basePrice),
        hasColor: el.dataset.hasColor === '1',
        hasSize: el.dataset.hasSize === '1',
        variants: json(el, 'data-variants', []) || [],
        color: null,
        size: null,
        variant: null
      };
    });

    function anyAvailable(row, filter) {
      for (var i = 0; i < row.variants.length; i++) {
        var v = row.variants[i];
        if (!v.available) continue;
        if (filter(v)) return true;
      }
      return false;
    }

    /* The buyable variant for this row's current picks, or null. Every axis the
       product actually has must be chosen; an unavailable match is not buyable.
       A stray third option resolves to its first available value. */
    function resolve(row) {
      if (row.hasColor && !row.color) return null;
      if (row.hasSize && !row.size) return null;
      for (var i = 0; i < row.variants.length; i++) {
        var v = row.variants[i];
        if (row.hasColor && v.color !== row.color) continue;
        if (row.hasSize && v.size !== row.size) continue;
        if (v.available) return v;
      }
      return null;
    }

    /* Swap the row image to the chosen color's product shot. */
    function setImage(row) {
      var img = row.el.querySelector('[data-bpair-img]');
      if (!img || !row.color) return;
      for (var i = 0; i < row.variants.length; i++) {
        if (row.variants[i].color === row.color && row.variants[i].img) {
          img.src = row.variants[i].img;
          return;
        }
      }
    }

    function paintOptions(row) {
      each(row.el.querySelectorAll('[data-bpair-sw]'), function (btn) {
        var c = btn.getAttribute('data-color');
        var live = anyAvailable(row, function (v) { return v.color === c; });
        btn.classList.toggle('is-out', !live);
        btn.classList.toggle('is-on', c === row.color);
        btn.setAttribute('aria-pressed', c === row.color ? 'true' : 'false');
      });
      each(row.el.querySelectorAll('[data-bpair-size]'), function (btn) {
        var s = btn.getAttribute('data-size');
        var live = anyAvailable(row, function (v) {
          return v.size === s && (!row.hasColor || !row.color || v.color === row.color);
        });
        btn.classList.toggle('is-out', !live);
        btn.classList.toggle('is-on', s === row.size);
        btn.setAttribute('aria-pressed', s === row.size ? 'true' : 'false');
      });
    }

    function update() {
      rows.forEach(function (row) {
        row.variant = resolve(row);
        paintOptions(row);
        var dead = !anyAvailable(row, function () { return true; });
        row.el.classList.toggle('is-dead', dead);
        var cue = row.el.querySelector('[data-bpair-cue]');
        if (cue) {
          if (dead) { cue.hidden = false; cue.textContent = 'Sold out'; }
          else if (row.variant) { cue.hidden = true; }
          else if (row.hasSize && !row.size) { cue.hidden = false; cue.textContent = 'Select a size'; }
          else { cue.hidden = false; cue.textContent = 'That combination is sold out'; }
        }
      });

      var a = rows[0], b = rows[1];
      var grossA = a.variant ? a.variant.price : a.basePrice;
      var grossB = b.variant ? b.variant.price : b.basePrice;
      var nowA = Math.max(0, grossA - a.saleCents);
      var nowB = Math.max(0, grossB - b.saleCents);
      var offs = pairOff(nowA, nowB, dType, dValue);

      var gross = grossA + grossB;
      var net = (nowA - offs[0]) + (nowB - offs[1]);
      var save = gross - net;

      setPrice(a, grossA, nowA - offs[0]);
      setPrice(b, grossB, nowB - offs[1]);

      var wasEl = root.querySelector('[data-bpair-was]');
      var nowEl = root.querySelector('[data-bpair-now]');
      var pillEl = root.querySelector('[data-bpair-pill]');
      if (wasEl) wasEl.textContent = save > 0 ? money(gross) : '';
      if (nowEl) nowEl.textContent = money(net);
      if (pillEl) pillEl.textContent = save > 0 ? 'You save ' + money(save) : '';

      var ready = !!(a.variant && b.variant);
      cta.disabled = !ready;
      if (ready) paintCta(gross, net, save);
      else cta.textContent = ctaPrompt();
    }

    /* The wording, then the struck total, then the price: the same line the
       snippet rendered, rebuilt as nodes. textContent for the merchant's
       string is the escaping — an innerHTML template here would run whatever
       a merchant typed into the appearance editor on every shopper's page. */
    function paintCta(gross, net, save) {
      cta.textContent = ctaLabel + ' - ';
      if (save > 0) {
        var was = document.createElement('s');
        was.textContent = money(gross);
        cta.appendChild(was);
        cta.appendChild(document.createTextNode(' '));
      }
      var now = document.createElement('b');
      now.textContent = money(net);
      cta.appendChild(now);
    }

    function ctaPrompt() {
      var deadRow = false, unpicked = 0, soldOut = false;
      rows.forEach(function (row) {
        if (!anyAvailable(row, function () { return true; })) { deadRow = true; return; }
        if (row.variant) return;
        if ((row.hasSize && !row.size) || (row.hasColor && !row.color)) unpicked++;
        else soldOut = true;
      });
      if (deadRow) return 'Bundle unavailable right now';
      if (unpicked > 0) return unpicked > 1 ? 'Select both sizes' : 'Select a size';
      if (soldOut) return 'Selected size sold out';
      return 'Select your options';
    }

    function setPrice(row, gross, final) {
      var el = row.el.querySelector('[data-bpair-price]');
      if (!el) return;
      el.innerHTML = final < gross
        ? '<s>' + money(gross) + '</s><b>' + money(final) + '</b>'
        : money(gross);
    }

    /* --- option clicks --- */
    rows.forEach(function (row) {
      each(row.el.querySelectorAll('[data-bpair-sw]'), function (btn) {
        btn.addEventListener('click', function () {
          if (btn.classList.contains('is-out')) return;
          row.color = btn.getAttribute('data-color');
          /* Keep the size if it still exists in the new color. */
          if (row.size && !anyAvailable(row, function (v) {
            return v.size === row.size && v.color === row.color;
          })) row.size = null;
          setImage(row);
          update();
        });
      });
      each(row.el.querySelectorAll('[data-bpair-size]'), function (btn) {
        btn.addEventListener('click', function () {
          if (btn.classList.contains('is-out')) return;
          row.size = btn.getAttribute('data-size');
          update();
        });
      });
    });

    /* --- add both lines in ONE call, one shared fresh _bundle_id --- */
    cta.addEventListener('click', function () {
      if (cta.disabled) return;
      var a = rows[0], b = rows[1];
      if (!a.variant || !b.variant) return;
      if (msg) msg.textContent = '';

      var bundleId = newBundleId();
      var props = { _bundle_id: bundleId, _bundle_kind: 'pair' };
      var items = [
        { id: a.variant.id, quantity: 1, properties: props },
        { id: b.variant.id, quantity: 1, properties: props }
      ];

      var before = cta.innerHTML;
      cta.disabled = true;
      cta.textContent = 'Adding';
      window.neswAddToCartItems(items, { openDrawer: true })
        .then(function () {
          track('bundle_add_to_cart', { bundle_kind: 'pair', lines: items.length });
          cta.innerHTML = before;
          cta.disabled = false;
        })
        .catch(function () {
          cta.innerHTML = before;
          cta.disabled = false;
          if (msg) msg.textContent = 'Could not add the bundle. An item may be out of stock.';
        });
    });

    /* --- initial selection ---------------------------------------------
       Page-product row starts on whatever the shopper is already looking at,
       read from the PDP controls (read only, never clicked, so the gallery
       sync above is untouched). Everything else starts on its first
       available variant. */
    function handleize(s) {
      return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }
    function pdpColorHandle() {
      var sw = document.querySelector('.product-color-swatch.w--ecommerce-pill-selected');
      return sw ? (sw.getAttribute('data-option-name') || '').trim().toLowerCase() : '';
    }
    function pdpSize() {
      var p = document.querySelector('.add-to-cart-section .w-commerce-commerceaddtocartoptionpill:not(.product-color-swatch).w--ecommerce-pill-selected');
      return p ? (p.textContent || '').trim() : '';
    }

    rows.forEach(function (row) {
      var seed = null;
      for (var i = 0; i < row.variants.length; i++) {
        if (row.variants[i].available) { seed = row.variants[i]; break; }
      }
      if (row.hasColor) row.color = seed ? seed.color : null;
      if (row.hasSize) row.size = seed ? seed.size : null;

      if (row.role === 'page') {
        var pc = pdpColorHandle();
        if (row.hasColor && pc) {
          for (var j = 0; j < row.variants.length; j++) {
            if (handleize(row.variants[j].color) === pc) { row.color = row.variants[j].color; break; }
          }
        }
        var ps = pdpSize();
        if (row.hasSize && ps) {
          for (var k = 0; k < row.variants.length; k++) {
            if (row.variants[k].size === ps && row.variants[k].available) { row.size = ps; break; }
          }
        }
      }
      setImage(row);
    });

    update();
  }

  /* ------------------------------------------------------------- bootstrap */

  function boot() {
    each(document.querySelectorAll('[data-btier]'), initTiers);
    each(document.querySelectorAll('[data-bpair]'), initPair);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
